/**
 * Kinng.app — API helper.
 *
 * Estratégia: API é a fonte de verdade; localStorage funciona como CACHE.
 *
 * Sessões separadas:
 *   • Tenant/afiliado: kinng.token / kinng.me
 *   • Super-Admin:     kinng.admin_token / kinng.admin_me
 * Isso permite manter as duas sessões abertas simultaneamente sem interferência
 * (ex.: super-admin acessando /admin enquanto alguém se cadastra via /register).
 * Requests para rotas /api/admin/* usam preferencialmente o admin_token.
 */

const API_BASE = (
    process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/$/, "") + "/api";

const TOKEN_KEY = "kinng.token";
const ME_CACHE_KEY = "kinng.me";
const ADMIN_TOKEN_KEY = "kinng.admin_token";
const ADMIN_ME_CACHE_KEY = "kinng.admin_me";

const safeGet = (k) => { try { return localStorage.getItem(k); } catch (_) { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch (_) { /* ignore */ } };
const safeDel = (k) => { try { localStorage.removeItem(k); } catch (_) { /* ignore */ } };

export const auth = {
    // Tenant / afiliado
    getToken: () => safeGet(TOKEN_KEY),
    setToken: (t) => safeSet(TOKEN_KEY, t),
    clear: () => { safeDel(TOKEN_KEY); safeDel(ME_CACHE_KEY); },
    cacheMe: (me) => safeSet(ME_CACHE_KEY, JSON.stringify(me)),
    readCachedMe: () => {
        const raw = safeGet(ME_CACHE_KEY);
        try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
    },
    // Super-admin
    getAdminToken: () => safeGet(ADMIN_TOKEN_KEY),
    setAdminToken: (t) => safeSet(ADMIN_TOKEN_KEY, t),
    clearAdmin: () => { safeDel(ADMIN_TOKEN_KEY); safeDel(ADMIN_ME_CACHE_KEY); },
    cacheAdminMe: (me) => safeSet(ADMIN_ME_CACHE_KEY, JSON.stringify(me)),
    readCachedAdminMe: () => {
        const raw = safeGet(ADMIN_ME_CACHE_KEY);
        try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
    },
    // Clear all
    clearAll: () => { safeDel(TOKEN_KEY); safeDel(ME_CACHE_KEY); safeDel(ADMIN_TOKEN_KEY); safeDel(ADMIN_ME_CACHE_KEY); },
};

const isAdminPath = (path) =>
    path.startsWith("/admin/") ||
    path.startsWith("/admin?") ||
    path === "/admin" ||
    path.startsWith("/auth/admin/");

const buildHeaders = (path, extra = {}) => {
    const h = { "Content-Type": "application/json", ...extra };
    const token = isAdminPath(path)
        ? (auth.getAdminToken() || auth.getToken())
        : (auth.getToken() || auth.getAdminToken());
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
};

const parseResponse = async (res, path) => {
    let payload = null;
    try { payload = await res.json(); } catch (_) { /* empty body */ }
    if (!res.ok) {
        // Interceptor global de 401: limpa tokens e força reload no login apropriado.
        // Ignoramos rotas de LOGIN (senha errada não deve recarregar a página) e
        // rotas realmente públicas (register/book/afiliado).
        if (res.status === 401 && typeof window !== "undefined") {
            const isLoginEndpoint = path === "/auth/login" || path === "/auth/admin/login";
            const goingToAdmin = isAdminPath(path || "");
            if (!isLoginEndpoint) {
                if (goingToAdmin) {
                auth.clearAdmin();
                if (!window.location.pathname.startsWith("/admin")) {
                    window.location.href = "/admin";
                } else {
                    // Já em /admin — força reload para resetar o React state.
                    window.location.reload();
                }
            } else {
                auth.clear();
                const path_ = window.location.pathname;
                // Rotas PÚBLICAS (não autenticadas) que não devem redirecionar.
                // NOTA: '/' NÃO entra aqui porque o CRM autenticado do tenant
                // também vive em '/', então precisamos forçar reload para
                // resetar SalonProvider.loggedIn.
                const publicRoots = ["/register", "/afiliado", "/book"];
                const isPublic = publicRoots.some((r) => path_ === r || path_.startsWith(r + "/"));
                if (!isPublic) {
                    // Full reload para resetar SalonProvider (que também vive em '/').
                    if (path_ === "/") {
                        window.location.reload();
                    } else {
                        window.location.href = "/";
                    }
                }
            }
            }
        }
        // 402 Payment Required — o backend bloqueou o CRM por pagamento pendente.
        // NÃO desloga: apenas sinaliza o SalonProvider pra trocar pra tela de
        // pagamento. A detecção primária é via /tenant/settings.access_blocked;
        // isto cobre a corrida (bloqueio a meio de uma sessão já aberta).
        if (res.status === 402 && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("kinng:payment-required"));
        }
        const detail = payload && (payload.detail || payload.message);
        const detailStr =
            detail && typeof detail === "object" && !Array.isArray(detail)
                ? (detail.message || detail.error || JSON.stringify(detail))
                : detail;
        const msg = typeof detailStr === "string"
            ? detailStr
            : Array.isArray(detail)
                ? detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ")
                : `Erro ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        err.payload = payload;
        throw err;
    }
    return payload;
};

export const api = {
    get: (path) => fetch(`${API_BASE}${path}`, { headers: buildHeaders(path) }).then((r) => parseResponse(r, path)),
    post: (path, body) => fetch(`${API_BASE}${path}`, {
        method: "POST", headers: buildHeaders(path), body: JSON.stringify(body || {}),
    }).then((r) => parseResponse(r, path)),
    patch: (path, body) => fetch(`${API_BASE}${path}`, {
        method: "PATCH", headers: buildHeaders(path), body: JSON.stringify(body || {}),
    }).then((r) => parseResponse(r, path)),
    del: (path) => fetch(`${API_BASE}${path}`, {
        method: "DELETE", headers: buildHeaders(path),
    }).then((r) => parseResponse(r, path)),
};

// ─── High-level auth helpers ─────────────────────────────────────────────
export const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res && res.token) {
        auth.setToken(res.token);
        auth.cacheMe({ kind: "tenant_user", user: res.user, tenant: res.tenant });
    }
    return res;
};

export const adminLogin = async (email, password) => {
    const res = await api.post("/auth/admin/login", { email, password });
    if (res && res.token) {
        auth.setAdminToken(res.token);
        auth.cacheAdminMe({ kind: "admin", user: res.user });
    }
    return res;
};

export const fetchMe = async () => {
    if (!auth.getToken()) return null;
    try {
        const me = await api.get("/auth/me");
        auth.cacheMe(me);
        return me;
    } catch (e) {
        if (e.status === 401) auth.clear();
        return auth.readCachedMe();
    }
};

export const logout = () => auth.clear();
export const adminLogout = () => auth.clearAdmin();

export default api;
